
import React, { useState, useRef, useEffect } from 'react';
import { Calendar, Clock, X } from 'lucide-react';

const DateFilterComponent = ({ onDateFilterChange, cardClasses, isDarkMode, currentFilter }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filterType, setFilterType] = useState(currentFilter?.type || 'relative');
  const [relativeAmount, setRelativeAmount] = useState(currentFilter?.amount || 30);
  const [relativeUnit, setRelativeUnit] = useState(currentFilter?.unit || 'days');
  const [startDate, setStartDate] = useState(currentFilter?.startDate || '');
  const [endDate, setEndDate] = useState(currentFilter?.endDate || '');
  const dropdownRef = useRef(null);

  // Opciones predefinidas para filtros relativos
  const quickOptions = [
    { label: '5 min', amount: 5, unit: 'minutes' },
    { label: '15 min', amount: 15, unit: 'minutes' },
    { label: '30 min', amount: 30, unit: 'minutes' },
    { label: '1 hora', amount: 1, unit: 'hours' },
    { label: '3 horas', amount: 3, unit: 'hours' },
    { label: '6 horas', amount: 6, unit: 'hours' },
    { label: '12 horas', amount: 12, unit: 'hours' },
    { label: '1 día', amount: 1, unit: 'days' },
    { label: '7 días', amount: 7, unit: 'days' },
    { label: '30 días', amount: 30, unit: 'days' },
    { label: '90 días', amount: 90, unit: 'days' }
  ];

  const unitOptions = [
    { value: 'minutes', label: 'Minutos' },
    { value: 'hours', label: 'Horas' },
    { value: 'days', label: 'Días' },
    { value: 'weeks', label: 'Semanas' },
    { value: 'months', label: 'Meses' }
  ];

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleApply = () => {
    const newFilter = {
      type: filterType,
      ...(filterType === 'relative' 
        ? { amount: relativeAmount, unit: relativeUnit }
        : { startDate, endDate }
      )
    };
    
    onDateFilterChange(newFilter);
    setIsOpen(false);
  };

  const handleQuickOption = (option) => {
    setFilterType('relative');
    setRelativeAmount(option.amount);
    setRelativeUnit(option.unit);
    
    const newFilter = {
      type: 'relative',
      amount: option.amount,
      unit: option.unit
    };
    
    onDateFilterChange(newFilter);
    setIsOpen(false);
  };

  const getCurrentFilterLabel = () => {
    if (currentFilter?.type === 'absolute') {
      return `${currentFilter.startDate} - ${currentFilter.endDate}`;
    } else {
      const amount = currentFilter?.amount || 30;
      const unit = currentFilter?.unit || 'days';
      const unitLabel = unitOptions.find(u => u.value === unit)?.label?.toLowerCase() || unit;
      return `${amount} ${unitLabel}`;
    }
  };

  const inputClasses = `w-full px-3 py-2 rounded-lg border ${
    isDarkMode 
      ? 'bg-gray-800 border-gray-600 text-white focus:border-blue-400' 
      : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
  } focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all`;

  const optionClasses = isDarkMode ? 'bg-[#1b1038] text-violet-50' : 'bg-white text-slate-900';

  const buttonClasses = `px-3 py-1 rounded-md transition-all ${
    isDarkMode
      ? 'bg-gray-700 hover:bg-gray-600 text-white'
      : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
  }`;

  const activeButtonClasses = `px-3 py-1 rounded-md transition-all bg-blue-500 hover:bg-blue-600 text-white`;

  return (
    <div className="relative z-[100]" ref={dropdownRef}>
      {/* Botón principal - más compacto para el header */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`p-3 rounded-full ${cardClasses} hover:scale-105 transition-all duration-200 flex items-center justify-center`}
        title={`Filtro de fechas: ${getCurrentFilterLabel()}`}
      >
        <Calendar className="w-5 h-5" />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          className={`absolute top-full right-0 mt-2 w-96 rounded-xl shadow-2xl z-50 p-6
            ${isDarkMode 
              ? 'bg-neutral-900/95 border border-gray-700' 
              : 'bg-white/95 border border-gray-300'
            }`}
        >
          {/* Header del dropdown */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Filtro de Fechas</h3>
            <button
              onClick={() => setIsOpen(false)}
              className={`p-1 rounded-md hover:bg-gray-200 ${isDarkMode ? 'hover:bg-gray-700' : ''} transition-colors`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Filtro actual */}
          <div className="mb-4 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
            <p className="text-sm font-medium text-blue-400 mb-1">Filtro actual:</p>
            <p className="text-sm opacity-80">{getCurrentFilterLabel()}</p>
          </div>

          {/* Tabs */}
          <div className="flex space-x-2 mb-4">
            <button
              onClick={() => setFilterType('relative')}
              className={filterType === 'relative' ? activeButtonClasses : buttonClasses}
            >
              <Clock className="w-4 h-4 inline mr-1" />
              Relativo
            </button>
            <button
              onClick={() => setFilterType('absolute')}
              className={filterType === 'absolute' ? activeButtonClasses : buttonClasses}
            >
              <Calendar className="w-4 h-4 inline mr-1" />
              Absoluto
            </button>
          </div>

          {filterType === 'relative' ? (
            <div className="space-y-4">
              {/* Opciones rápidas */}
              <div>
                <label className="block text-sm font-medium mb-2 opacity-80">
                  Opciones rápidas
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {quickOptions.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleQuickOption(option)}
                      className={`${buttonClasses} text-xs py-1 hover:bg-blue-500 hover:text-white transition-colors duration-200`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Configuración personalizada */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-2 opacity-80">
                    Cantidad
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="999"
                    value={relativeAmount}
                    onChange={(e) => setRelativeAmount(parseInt(e.target.value) || 1)}
                    className={inputClasses}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 opacity-80">
                    Unidad
                  </label>
                  <select
                    value={relativeUnit}
                    onChange={(e) => setRelativeUnit(e.target.value)}
                    className={inputClasses}
                  >
                    {unitOptions.map((unit) => (
                      <option className={optionClasses} key={unit.value} value={unit.value}>
                        {unit.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-2 opacity-80">
                    Fecha inicio
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className={inputClasses}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 opacity-80">
                    Fecha fin
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className={inputClasses}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Botones de acción */}
          <div className="flex justify-between items-center mt-6 pt-4 border-t border-opacity-20 border-gray-400">
            <button
              onClick={() => {
                setFilterType('relative');
                setRelativeAmount(30);
                setRelativeUnit('days');
                setStartDate('');
                setEndDate('');
                const defaultFilter = { type: 'relative', amount: 30, unit: 'days' };
                onDateFilterChange(defaultFilter);
                setIsOpen(false);
              }}
              className={`${buttonClasses} flex items-center space-x-1`}
            >
              <X className="w-4 h-4" />
              <span>Reset</span>
            </button>
            
            <div className="flex space-x-2">
              <button
                onClick={() => setIsOpen(false)}
                className={buttonClasses}
              >
                Cancelar
              </button>
              <button
                onClick={handleApply}
                disabled={
                  (filterType === 'absolute' && (!startDate || !endDate)) ||
                  (filterType === 'relative' && (!relativeAmount || relativeAmount < 1))
                }
                className="px-4 py-1 rounded-md bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed text-white transition-all"
              >
                Aplicar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateFilterComponent;